/**
 * AdminArtifactsTable - Full artifacts view for admins
 * 
 * Features:
 * - View ALL artifacts across all creators
 * - Filter by creator
 * - Search by artifact title
 * - Sort by metrics (sessions, completions, efficiency, etc.)
 */

import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BookOpen, Search, ArrowUpDown, ArrowUp, ArrowDown, Users, Target, TrendingUp, CheckCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAllCreatorAccounts, useAllTrainingProviders, type CreatorAccount } from "@/hooks/useCreatorAdminAnalytics";

type SortField = 'title' | 'creator' | 'sessions' | 'completions' | 'technicians' | 'shops' | 'efficiency' | 'confidence';
type SortDir = 'asc' | 'desc';

interface ArtifactWithMetrics {
  id: string;
  title: string;
  provider_name: string;
  provider_id: string | null;
  artifact_type: string;
  skill_depth: string;
  is_active: boolean;
  creators: { id: string; name: string }[];
  metrics: {
    total_sessions: number;
    verified_completions: number;
    likely_completions: number;
    sample_technicians: number;
    sample_shops: number;
    avg_efficiency_delta_percent: number | null;
    avg_confidence_delta: number | null;
  } | null;
}

export function AdminArtifactsTable() {
  const { data: creators } = useAllCreatorAccounts();
  const { data: providers } = useAllTrainingProviders();
  const [search, setSearch] = useState("");
  const [creatorFilter, setCreatorFilter] = useState<string>("all");
  const [providerFilter, setProviderFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<SortField>('sessions');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Fetch all artifacts with their metrics and creator links
  const { data: artifactsWithMetrics, isLoading } = useQuery({
    queryKey: ['admin-all-artifacts-with-metrics'],
    queryFn: async () => {
      // Get all artifacts
      const { data: artifacts, error: artifactsError } = await supabase
        .from('training_artifacts')
        .select('id, title, provider_name, provider_id, artifact_type, skill_depth, is_active')
        .order('title');

      if (artifactsError) throw artifactsError;

      // Get all metrics
      const { data: metrics, error: metricsError } = await supabase
        .from('training_artifact_global_metrics')
        .select('*');

      if (metricsError) throw metricsError;

      // Get all creator-artifact links with creator names
      const { data: links, error: linksError } = await supabase
        .from('training_creator_artifacts')
        .select(`
          artifact_id,
          training_creator_accounts (
            id,
            name
          )
        `);

      if (linksError) throw linksError;

      // Build lookup maps
      const metricsMap = new Map(
        (metrics || []).map(m => [m.artifact_id, m])
      );

      const creatorsMap = new Map<string, { id: string; name: string }[]>();
      (links || []).forEach((link: any) => {
        const existing = creatorsMap.get(link.artifact_id) || [];
        if (link.training_creator_accounts) {
          existing.push({
            id: link.training_creator_accounts.id,
            name: link.training_creator_accounts.name,
          });
        }
        creatorsMap.set(link.artifact_id, existing);
      });

      return (artifacts || []).map(a => ({
        ...a,
        creators: creatorsMap.get(a.id) || [],
        metrics: metricsMap.get(a.id) || null,
      })) as ArtifactWithMetrics[];
    },
  });

  // Filter and sort
  const filteredAndSorted = useMemo(() => {
    if (!artifactsWithMetrics) return [];

    let result = [...artifactsWithMetrics];

    // Search filter
    if (search) {
      const lowerSearch = search.toLowerCase();
      result = result.filter(a => 
        a.title.toLowerCase().includes(lowerSearch) ||
        a.provider_name?.toLowerCase().includes(lowerSearch)
      );
    }

    // Creator filter
    if (creatorFilter !== 'all') {
      result = result.filter(a => 
        a.creators.some(c => c.id === creatorFilter)
      );
    }

    // Provider filter
    if (providerFilter !== 'all') {
      result = result.filter(a => a.provider_id === providerFilter);
    }

    // Sort
    result.sort((a, b) => {
      let aVal: number | string = 0;
      let bVal: number | string = 0;

      switch (sortField) {
        case 'title':
          aVal = a.title.toLowerCase();
          bVal = b.title.toLowerCase();
          break;
        case 'creator':
          aVal = a.creators[0]?.name?.toLowerCase() || 'zzz';
          bVal = b.creators[0]?.name?.toLowerCase() || 'zzz';
          break;
        case 'sessions':
          aVal = a.metrics?.total_sessions || 0;
          bVal = b.metrics?.total_sessions || 0;
          break;
        case 'completions':
          aVal = (a.metrics?.verified_completions || 0) + (a.metrics?.likely_completions || 0);
          bVal = (b.metrics?.verified_completions || 0) + (b.metrics?.likely_completions || 0);
          break;
        case 'technicians':
          aVal = a.metrics?.sample_technicians || 0;
          bVal = b.metrics?.sample_technicians || 0;
          break;
        case 'shops':
          aVal = a.metrics?.sample_shops || 0;
          bVal = b.metrics?.sample_shops || 0;
          break;
        case 'efficiency':
          aVal = a.metrics?.avg_efficiency_delta_percent ?? -999;
          bVal = b.metrics?.avg_efficiency_delta_percent ?? -999;
          break;
        case 'confidence':
          aVal = a.metrics?.avg_confidence_delta ?? -999;
          bVal = b.metrics?.avg_confidence_delta ?? -999;
          break;
      }

      if (typeof aVal === 'string') {
        return sortDir === 'asc' 
          ? aVal.localeCompare(bVal as string)
          : (bVal as string).localeCompare(aVal);
      }
      
      return sortDir === 'asc' 
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });

    return result;
  }, [artifactsWithMetrics, search, creatorFilter, providerFilter, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return sortDir === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  const formatNumber = (n: number | null | undefined) => {
    if (n === null || n === undefined) return '—';
    return n.toLocaleString();
  };

  const formatPercent = (n: number | null | undefined) => {
    if (n === null || n === undefined) return '—';
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(1)}%`;
  };

  const formatDelta = (n: number | null | undefined) => {
    if (n === null || n === undefined) return '—';
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Loading artifacts...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              All Training Artifacts
            </CardTitle>
            <CardDescription>
              {filteredAndSorted.length} of {artifactsWithMetrics?.length || 0} artifacts
            </CardDescription>
          </div>
        </div>
        
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search artifacts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={creatorFilter} onValueChange={setCreatorFilter}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="All Creators" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Creators</SelectItem>
              {creators?.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={providerFilter} onValueChange={setProviderFilter}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="All Providers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Providers</SelectItem>
              {providers?.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.display_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Button variant="ghost" size="sm" onClick={() => handleSort('title')} className="h-auto p-0 font-medium">
                    Artifact <SortIcon field="title" />
                  </Button>
                </TableHead>
                <TableHead>
                  <Button variant="ghost" size="sm" onClick={() => handleSort('creator')} className="h-auto p-0 font-medium">
                    Creator <SortIcon field="creator" />
                  </Button>
                </TableHead>
                <TableHead className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => handleSort('sessions')} className="h-auto p-0 font-medium">
                    <Target className="h-3 w-3 mr-1" />
                    Sessions <SortIcon field="sessions" />
                  </Button>
                </TableHead>
                <TableHead className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => handleSort('completions')} className="h-auto p-0 font-medium">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Completions <SortIcon field="completions" />
                  </Button>
                </TableHead>
                <TableHead className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => handleSort('technicians')} className="h-auto p-0 font-medium">
                    <Users className="h-3 w-3 mr-1" />
                    Techs <SortIcon field="technicians" />
                  </Button>
                </TableHead>
                <TableHead className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => handleSort('efficiency')} className="h-auto p-0 font-medium">
                    <TrendingUp className="h-3 w-3 mr-1" />
                    Efficiency Δ <SortIcon field="efficiency" />
                  </Button>
                </TableHead>
                <TableHead className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => handleSort('confidence')} className="h-auto p-0 font-medium">
                    Confidence Δ <SortIcon field="confidence" />
                  </Button>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No artifacts found
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSorted.map(artifact => (
                  <TableRow key={artifact.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{artifact.title}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2">
                          <span>{artifact.provider_name}</span>
                          <Badge variant="outline" className="text-[10px] px-1 py-0">
                            {artifact.artifact_type}
                          </Badge>
                          {!artifact.is_active && (
                            <Badge variant="secondary" className="text-[10px] px-1 py-0">
                              Inactive
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {artifact.creators.length === 0 ? (
                        <span className="text-muted-foreground text-sm">Unlinked</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {artifact.creators.map(c => (
                            <Badge key={c.id} variant="secondary" className="text-xs">
                              {c.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatNumber(artifact.metrics?.total_sessions)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatNumber(
                        (artifact.metrics?.verified_completions || 0) + 
                        (artifact.metrics?.likely_completions || 0)
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatNumber(artifact.metrics?.sample_technicians)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      <span className={
                        artifact.metrics?.avg_efficiency_delta_percent != null
                          ? artifact.metrics.avg_efficiency_delta_percent > 0 
                            ? 'text-green-600' 
                            : artifact.metrics.avg_efficiency_delta_percent < 0 
                              ? 'text-red-600' 
                              : ''
                          : ''
                      }>
                        {formatPercent(artifact.metrics?.avg_efficiency_delta_percent)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      <span className={
                        artifact.metrics?.avg_confidence_delta != null
                          ? artifact.metrics.avg_confidence_delta > 0 
                            ? 'text-green-600' 
                            : artifact.metrics.avg_confidence_delta < 0 
                              ? 'text-red-600' 
                              : ''
                          : ''
                      }>
                        {formatDelta(artifact.metrics?.avg_confidence_delta)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
